type RenderOptions = {
  showCurrency?: boolean;
};
class Money {
  readonly currency: string;
  readonly amount: number;
  readonly isPositive: boolean;
  readonly isNegative: boolean;
  readonly isZero: boolean;

  constructor(amount: number, currency: string) {
    this.amount = amount;
    this.currency = currency;
    this.isPositive = amount > 0;
    this.isNegative = amount < 0;
    this.isZero = amount === 0;
  }

  static plus = (l: Money, r: Money): Money => {
    if (l.currency !== r.currency) {
      throw new Error("Adding multiple currencies together");
    }

    return new Money(l.amount + r.amount, l.currency);
  };

  static minus = (l: Money, r: Money): Money => {
    if (l.currency !== r.currency) {
      throw new Error("Adding multiple currencies together");
    }

    return new Money(l.amount - r.amount, l.currency);
  };

  static fromInt = (amount: number, currency: string): Money => {
    return new Money(amount, currency);
  };

  static fromDecimal = (amount: number, currency: string): Money => {
    return Money.fromInt(parseFloat((amount * 100).toFixed(2)), currency);
  };

  static fromServer = ({
    amount,
    currency,
  }: {
    amount: number;
    currency: string;
  }): Money => {
    return Money.fromInt(amount, currency);
  };

  static toServer = (money: Money): { amount: number; currency: string } => {
    return { amount: money.amount, currency: money.currency };
  };

  static zero = (currency: string): Money => Money.fromInt(0, currency);

  equals = (other: Money): boolean =>
    this.currency === other.currency && this.amount === other.amount;

  toInt = (): { amount: number; currency: string } => {
    return { amount: this.amount, currency: this.currency };
  };

  negative = (): Money => Money.fromInt(-1 * this.amount, this.currency);

  static toDecimalString = (money: Money, options?: RenderOptions): string => {
    let formatted: string;
    try {
      // Storage is uniform hundredths, so a currency's third decimal (KWD,
      // BHD) is unrepresentable; clamp display to two digits so rendered
      // strings round-trip through amount inputs, which parse typed digits
      // at min(2, the currency's Intl digits).
      const defaultDigits =
        new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: money.currency,
        }).resolvedOptions().maximumFractionDigits ?? 2;
      formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: money.currency,
        ...(defaultDigits > 2
          ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          : {}),
      }).format(money.amount / 100);
    } catch {
      // Non-ISO commodity currencies (TENNIS_CLASS, MILES) have no Intl
      // formatting; render the bare number with the code as a suffix.
      // Always two decimals: amount inputs parse displayed digits by the
      // currency's minor unit, so the rendering must round-trip.
      const bare = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(money.amount / 100);
      formatted =
        options?.showCurrency === false
          ? bare
          : `${bare} ${money.currency}`;
    }
    return options?.showCurrency === false
      ? formatted.replace(/[^0-9-,.]/g, "")
      : formatted;
  };
}

export default Money;
